/**
 * 维度筛选器组件
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { ReactSortable } from 'react-sortablejs';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { deepClone } from '@/pages/datalab/utils/utils';
import { useActionHandle } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/useActionHandle';
import { DimensionItem } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/Dimensions/dimension_item';
import { getFirstColumn, getFlatOptions } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/utils';
import { IDimensionFilterProps } from './types';
import AddButton from '../AddButton';
import './index.scss';

const DimensionFilter: React.FC<IDimensionFilterProps> = (props) => {
  const {
    className,
    title,
    required,
    value = [],
    allDimensions = [],
    optionDimensions = [],
    onChange
  } = props;
  const { formatMessage } = useIntl();

  // 使用内部状态管理维度列表
  const [columns, setColumns] = useState<IColumnInfo[]>(value || []);

  // 同步外部传入的 value
  useEffect(() => {
    setColumns(value || []);
  }, [value]);

  // 通知父组件状态变化
  const notifyFun = (columnDimensions: IColumnInfo[]) => {
    setColumns(columnDimensions);
    onChange?.(columnDimensions);
  };

  const handle = useActionHandle(columns, notifyFun);
  const { addItem, setItems } = handle;

  const selectedItems = useMemo(() => columns.map((item) => item.column_name), [columns]);

  // 所有可选维度
  const flatOptions = useMemo(
    () => getFlatOptions(optionDimensions || []),
    [optionDimensions]
  );
  // 所有可选指标已添加完时禁用添加按钮
  const disableAddButton = useMemo(
    () => flatOptions?.length === columns?.length,
    [flatOptions, columns]
  );

  // 处理添加维度
  const handleAddDimension = () => {
    if (disableAddButton) {
      return;
    }
    const newColumn = getFirstColumn(allDimensions || [], columns, flatOptions);
    if (!newColumn) {
      return;
    }
    addItem(newColumn);
  };

  return (
    <div className={classNames('dbl-dimension-filter', className)}>
      {title && (
        <div className={classNames('dbl-dimension-filter-title', { 'is-required': required })}>
          {title}
        </div>
      )}
      <div className="dbl-dimension-filter-content">
        <ReactSortable
          className="context-four"
          list={columns}
          setList={(list: IColumnInfo[]) => {
            setItems(list);
          }}
        >
          {(columns || []).map((column, index) => (
            <DimensionItem
              fieldSelectClassName="dbl-ab-experiment-field-select"
              key={`${column.table_id}.${column.column_name}`}
              optionDimensions={optionDimensions}
              column={column}
              index={index + 1}
              handle={handle}
              selectedItems={selectedItems}
              hasDimensionSelect={false}
              handleContent={() => null}
              onSelectItem={(newItemName: string) => {
                const newColumn = allDimensions.find((item: IColumnInfo) => item.column_name === newItemName);
                if (!newColumn || newColumn.column_name === column.column_name) {
                  return;
                }
                const list = [
                  ...columns.slice(0, index),
                  { ...deepClone(newColumn) },
                  ...columns.slice(index + 1, columns.length),
                ];
                setItems(list);
              }}
            />
          ))}
        </ReactSortable>
        <AddButton
          className={classNames('dbl-dimension-filter-add-button', { 'margin-t-10': columns?.length > 0 })}
          text={formatMessage({ id: 'dbl_ab_experiment_dimension_filter_add_button' })}
          onClick={handleAddDimension}
          disabled={disableAddButton}
        />
      </div>
    </div>
  );
};

export default DimensionFilter;
